import { useQuery } from '@apollo/client';
import { get as _get } from 'lodash';
import { useMemo } from 'react';

import type { DropdownOption } from '../../Dropdown';
import type { DropdownOptions } from '../config';
import { DROPDOWN_OPTIONS_CONFIG } from '../config';
import { prepareDropdownOptions, prepareQueryNodeAsDropdownOptions } from '../utils';

export type ReturnedHookType = {
  loading: boolean;
  options: DropdownOption<string>[];
  error?: string;
};

export const useFetchDropdownOptions = <T>(dropdownOption: DropdownOptions): ReturnedHookType => {
  const {
    queryName,
    schema,
    labelTransformationFunction,
    useQueryNodeFormat,
    additionalOptions,
  } = DROPDOWN_OPTIONS_CONFIG[dropdownOption];

  const { data, error, loading } = useQuery<T>(schema);

  const options: DropdownOption[] = useMemo(() => {
    const queryNameData = _get(data, queryName, []) ?? [];

    let result: DropdownOption[];
    if (useQueryNodeFormat) {
      result = prepareQueryNodeAsDropdownOptions(queryNameData, labelTransformationFunction);
    } else {
      result = prepareDropdownOptions(queryNameData, dropdownOption, labelTransformationFunction);
    }

    if (additionalOptions) {
      const resultValues = new Set(result.map((option) => option.value));
      const uniqueAdditionalOptions = additionalOptions.filter((option) => !resultValues.has(option.value));
      return [...result, ...uniqueAdditionalOptions];
    }

    return result;
  }, [data, queryName, dropdownOption, labelTransformationFunction, useQueryNodeFormat, additionalOptions]);

  return {
    loading,
    error: error?.message,
    options,
  };
};
